import Loading from '../common/loading.tsx';
import MarkerLightDownIcon from '../../assets/icons/marker-light-down.svg?react';
import PinnedNoteIcon from '../../assets/icons/pinned-note.svg?react';
import { cn } from '../../utils/common-utils.ts';
import { useHighlight } from '../../contexts/highlight-provider/useHighlight.ts';
import { QuizFragmentFragment } from '../../apollo/__generated__/graphql.ts';
import { useState } from 'react';
import BluebookCalculatorIcon from '../common/icons/bluebook-calculator-icon.tsx';
import BluebookReferenceIcon from '../common/icons/bluebook-reference-icon.tsx';
import Modal from '../common/modal.tsx';
import QuestionReportForm from '../questions/question-content/question-report-form.tsx';
import ReferenceModal from '../questions/question-content/reference-modal.tsx';
import AttemptMoreMenu from '../common/attempt-more-menu.tsx';

type IProps = {
  name?: string;
  loading: boolean;
  onExit: () => void;
  questionId?: number;
  questions: QuizFragmentFragment['quizQuestions'];
  questionIndex: number;
  openQuestionsModal: () => void;
  isMath: boolean;
  isCalc: boolean;
  onToggleCalculator: () => void;
};

const toolbarButtonClassName =
  'mx-0.5 flex h-[4rem] w-[4.4rem] flex-col items-center justify-center rounded-md border border-transparent bg-transparent px-1.5 text-[#222] transition-colors hover:bg-[rgba(254,219,0,0.18)] focus:border-none focus:outline-none';

const activeToolbarButtonClassName =
  'underline decoration-current underline-offset-4';

const toolButtonIconRowClassName = 'flex h-7 items-center justify-center';

const toolButtonLabelClassName =
  'mt-1.5 whitespace-nowrap text-xs font-semibold leading-none';

const attemptChromeDashStyle = {
  backgroundImage:
    'linear-gradient(to right, #111111 0 72%, transparent 72% 100%)',
  backgroundRepeat: 'repeat-x',
  backgroundSize: 'calc(100% / 56) 100%',
} as const;

const ReferenceToolbarIcon = () => (
  <BluebookReferenceIcon className="translate-y-px" />
);

export const QuizAttemptHeader = ({
  name,
  loading,
  onExit,
  questionId,
  questions,
  questionIndex,
  openQuestionsModal,
  isMath,
  isCalc,
  onToggleCalculator,
}: IProps) => {
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [isReferenceOpen, setIsReferenceOpen] = useState(false);
  const {
    setHighlightToggle,
    highlightToggle,
  } = useHighlight();
  const closeModals = () => {
    setIsReportOpen(false);
    setIsReferenceOpen(false);
  };

  return (
    <>
      <section
        className={'relative flex shrink-0 items-center justify-between bg-[#e7edf7] px-10 py-1.5'}
      >
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px]"
          style={attemptChromeDashStyle}
        />

        <div className="flex items-center">
          <div>{name}</div>
          {loading && <Loading isSmall={true} />}
        </div>

        <div className={'flex items-center gap-0.5'}>
          {!isMath && (
            <div
              className={cn(
                toolbarButtonClassName,
                'w-[6.9rem]',
                highlightToggle && activeToolbarButtonClassName,
              )}
              onClick={() => setHighlightToggle((prevState) => !prevState)}
            >
              <div className={cn(toolButtonIconRowClassName, 'gap-1')}>
                <MarkerLightDownIcon
                  width={18}
                  height={18}
                  fill={'currentColor'}
                />
                <PinnedNoteIcon
                  width={18}
                  height={18}
                  fill={'currentColor'}
                />
              </div>
              <div className={toolButtonLabelClassName}>Highlights & Notes</div>
            </div>
          )}

          {isMath && (
            <button
              className={cn(
                toolbarButtonClassName,
                'w-[5.3rem]',
                isCalc && activeToolbarButtonClassName,
              )}
              onClick={onToggleCalculator}
            >
              <div className={toolButtonIconRowClassName}>
                <BluebookCalculatorIcon className="h-[1.4rem] w-[1.4rem]" />
              </div>
              <div className={toolButtonLabelClassName}>Calculator</div>
            </button>
          )}

          {isMath && (
            <button
              className={cn(
                toolbarButtonClassName,
                isReferenceOpen && activeToolbarButtonClassName,
              )}
              onClick={() => setIsReferenceOpen(true)}
            >
              <div className={toolButtonIconRowClassName}>
                <ReferenceToolbarIcon />
              </div>
              <div className={toolButtonLabelClassName}>Reference</div>
            </button>
          )}

          <AttemptMoreMenu
            onExit={onExit}
            onOpenQuestions={openQuestionsModal}
            onOpenReport={() => setIsReportOpen(true)}
            questionCountLabel={`${questionIndex + 1}/${questions?.length}`}
          />
        </div>
      </section>

      <Modal
        title={'Question Report'}
        onClose={closeModals}
        isOpen={isReportOpen}
      >
        <QuestionReportForm questionId={questionId} close={closeModals} />
      </Modal>

      <ReferenceModal isOpen={isReferenceOpen} onClose={closeModals} />
    </>
  );
};
